import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Modal,
  Input,
  DatePicker,
  Select,
  Table,
  InputNumber,
  Row,
  Col,
  Divider,
  Typography,
  Alert,
  message,
  Spin,
  Button,
  Upload,
  Tag,
  Tooltip,
  Space
} from 'antd';
import { RobotOutlined, InboxOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import apiClient from '../utils/axiosConfig.ts';
import { unifiedShipmentsAPI, supplierAPI, customsAIImportAPI } from '../services/api.ts';
import {
  UnifiedShipment,
  Warehouse,
  ShipmentPackingListItem,
  GenerateCustomsDeclarationsPayload,
  RectificationReason
} from '../types.ts';
import { documentTypeLabel, documentTypeColor } from '../utils/documentTypes.ts';

const { Option } = Select;
const { Text } = Typography;

interface CustomsSplitModalProps {
  visible: boolean;
  shipment: UnifiedShipment | null;
  onCancel: () => void;
  onSuccess: () => void;
}

type DeclType = 'IM7' | 'IM4';

interface Declaration {
  key: string;
  document_type: DeclType;
  document_number: string;
  document_date: Dayjs | null;
  mnr_code: string;
  warehouse_id?: number;
  // Fattura da cui è nata la proposta (solo descrittivo)
  hint?: string;
}

/**
 * Genera le dichiarazioni doganali in BOZZA di una spedizione EXTRA_UE arrivata
 * (v3.9.0): una o più H2 (IM7, deposito doganale) e/o H1 (IM4, libera pratica in
 * magazzino nazionalizzato). Ogni riga si ripartisce tra le dichiarazioni; la
 * proposta iniziale è una dichiarazione per fattura del fornitore. I pezzi non
 * assegnati restano "da dichiarare" e potranno entrare con una dichiarazione
 * successiva. La merce entra con l'Elabora in Documenti di Carico.
 * Spedizione destinata a un magazzino NAZIONALIZZATO: solo H1.
 */
const CustomsSplitModal: React.FC<CustomsSplitModalProps> = ({
  visible,
  shipment,
  onCancel,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [items, setItems] = useState<ShipmentPackingListItem[]>([]);
  const [declarations, setDeclarations] = useState<Declaration[]>([]);
  // Quote per riga e dichiarazione: alloc[itemId][declKey] = pezzi
  const [alloc, setAlloc] = useState<Record<number, Record<string, number>>>({});
  // Pezzi realmente arrivati per riga (default = spediti; solo righe non ancora a documento)
  const [received, setReceived] = useState<Record<number, number>>({});
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [reasons, setReasons] = useState<RectificationReason[]>([]);
  const [reasonCode, setReasonCode] = useState<string>('CUSTOMS_COUNT');
  const [reasonNotes, setReasonNotes] = useState<string>('');
  const keyCounter = useRef(0);
  // Importa con AI: bollette lette dall'AI → precompilazione. NAVI propone, non decide.
  const [aiVisible, setAiVisible] = useState(false);
  const [aiFiles, setAiFiles] = useState<any[]>([]);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiResult, setAiResult] = useState<any | null>(null);
  const [aiExisting, setAiExisting] = useState<string | null>(null);
  // Destinazione nazionalizzata: tutta la merce entra con l'H1 (niente deposito doganale)
  const onlyIM4 = !!shipment?.warehouse_is_nationalized;
  const defaultType: DeclType = onlyIM4 ? 'IM4' : 'IM7';

  const newKey = () => {
    keyCounter.current += 1;
    return `D${keyCounter.current}`;
  };

  const newDeclaration = (type: DeclType, hint?: string): Declaration => ({
    key: newKey(),
    document_type: type,
    document_number: '',
    document_date: dayjs(),
    mnr_code: '',
    warehouse_id: type === 'IM4' && onlyIM4 && shipment ? shipment.warehouse_id : undefined,
    hint,
  });

  const already = (it: ShipmentPackingListItem) => it.quantity_in_documents || 0;
  const canReceive = (it: ShipmentPackingListItem) => already(it) === 0;
  const receivedOf = (it: ShipmentPackingListItem) => (canReceive(it) ? (received[it.id] ?? it.quantity) : it.quantity);
  // Pezzi dichiarabili: riga non ancora a documento (con il ricevuto se la riga è intatta),
  // limitati dal server alla merce della partita davvero in transito (quantity_declarable)
  const availableOf = (it: ShipmentPackingListItem) => (canReceive(it)
    ? Math.max(0, receivedOf(it))
    : Math.max(0, Math.min(it.quantity - already(it), it.quantity_declarable ?? (it.quantity - already(it)))));
  const allocatedOf = (it: ShipmentPackingListItem) => Object.values(alloc[it.id] || {}).reduce((a, b) => a + (b || 0), 0);
  const remainingOf = (it: ShipmentPackingListItem) => availableOf(it) - allocatedOf(it);

  useEffect(() => {
    if (visible && shipment) {
      keyCounter.current = 0;
      setAiResult(null); setAiFiles([]); setAiExisting(null);
      setReasonCode('CUSTOMS_COUNT'); setReasonNotes('');
      loadData(shipment.id);
      supplierAPI.getRectificationReasons().then(setReasons).catch(() => setReasons([]));
    }
  }, [visible, shipment]); // eslint-disable-line react-hooks/exhaustive-deps

  // Proposta iniziale: una dichiarazione per fattura del fornitore (righe senza
  // fattura in una dichiarazione a parte); senza fatture una sola dichiarazione
  const buildProposal = (loadedItems: ShipmentPackingListItem[]) => {
    const declarable = (it: ShipmentPackingListItem) => Math.max(0, it.quantity_declarable ?? (it.quantity - (it.quantity_in_documents || 0)));
    const open = loadedItems.filter((it) => declarable(it) > 0);
    const groups: string[] = [];
    open.forEach((it) => {
      const g = it.invoice_number || '';
      if (!groups.includes(g)) groups.push(g);
    });
    const decls: Declaration[] = [];
    const byGroup: Record<string, string> = {};
    if (groups.length <= 1) {
      const d = newDeclaration(defaultType, groups[0] ? `Fattura ${groups[0]}` : undefined);
      decls.push(d);
      groups.forEach((g) => { byGroup[g] = d.key; });
    } else {
      groups.forEach((g) => {
        const d = newDeclaration(defaultType, g ? `Fattura ${g}` : 'Righe senza fattura');
        decls.push(d);
        byGroup[g] = d.key;
      });
    }
    if (!decls.length) decls.push(newDeclaration(defaultType));
    const a: Record<number, Record<string, number>> = {};
    open.forEach((it) => {
      a[it.id] = { [byGroup[it.invoice_number || '']]: declarable(it) };
    });
    setDeclarations(decls);
    setAlloc(a);
  };

  const loadData = async (shipmentId: number) => {
    setLoadingData(true);
    try {
      const [packing, warehousesResponse] = await Promise.all([
        unifiedShipmentsAPI.getPackingList(shipmentId),
        apiClient.get('/warehouses')
      ]);
      const loadedItems: ShipmentPackingListItem[] = packing?.items || [];
      setItems(loadedItems);
      const initialReceived: Record<number, number> = {};
      loadedItems.forEach((it) => { initialReceived[it.id] = it.quantity; });
      setReceived(initialReceived);
      setWarehouses(warehousesResponse.data || []);
      buildProposal(loadedItems);
    } catch (error) {
      console.error('Errore nel caricamento dei dati spedizione:', error);
      message.error('Errore nel caricamento dei prodotti della spedizione');
    } finally {
      setLoadingData(false);
    }
  };

  const updateDeclaration = (key: string, patch: Partial<Declaration>) => {
    setDeclarations((prev) => prev.map((d) => (d.key === key ? { ...d, ...patch } : d)));
  };

  const addDeclaration = (type: DeclType) => {
    setDeclarations((prev) => [...prev, newDeclaration(type)]);
  };

  const removeDeclaration = (key: string) => {
    setDeclarations((prev) => prev.filter((d) => d.key !== key));
    setAlloc((prev) => {
      const next: Record<number, Record<string, number>> = {};
      Object.entries(prev).forEach(([id, row]) => {
        const { [key]: _removed, ...rest } = row;
        next[Number(id)] = rest;
      });
      return next;
    });
  };

  const setAllocQty = (it: ShipmentPackingListItem, key: string, value: number | null) => {
    let v = value || 0;
    if (v < 0) v = 0;
    const others = allocatedOf(it) - ((alloc[it.id] || {})[key] || 0);
    if (v > availableOf(it) - others) v = Math.max(0, availableOf(it) - others);
    setAlloc((prev) => ({ ...prev, [it.id]: { ...(prev[it.id] || {}), [key]: v } }));
  };

  // Tutti i pezzi ancora da dichiarare della riga in una sola dichiarazione
  const assignAll = (it: ShipmentPackingListItem, key: string) => {
    setAlloc((prev) => ({ ...prev, [it.id]: { [key]: availableOf(it) } }));
  };

  const setReceivedQty = (it: ShipmentPackingListItem, value: number | null) => {
    let v = value ?? 0;
    if (v < 1) v = 1;
    setReceived((prev) => ({ ...prev, [it.id]: v }));
    // se il ricevuto scende sotto le quote già assegnate, riduce le quote (dall'ultima)
    setAlloc((prev) => {
      const row = { ...(prev[it.id] || {}) };
      let excess = Object.values(row).reduce((a, b) => a + (b || 0), 0) - v;
      const keys = Object.keys(row).reverse();
      for (const k of keys) {
        if (excess <= 0) break;
        const take = Math.min(row[k] || 0, excess);
        row[k] = (row[k] || 0) - take;
        excess -= take;
      }
      return { ...prev, [it.id]: row };
    });
  };

  const totals = useMemo(() => {
    const byDecl: Record<string, number> = {};
    let remaining = 0;
    let spedito = 0;
    let gia = 0;
    const diffLines: string[] = [];
    items.forEach((it) => {
      spedito += it.quantity;
      gia += already(it);
      Object.entries(alloc[it.id] || {}).forEach(([k, q]) => { byDecl[k] = (byDecl[k] || 0) + (q || 0); });
      remaining += remainingOf(it);
      const r = receivedOf(it);
      if (r !== it.quantity) {
        const d = r - it.quantity;
        diffLines.push(`${it.product_code || `ID ${it.product_id}`}: ${it.quantity} → ${r} (${d > 0 ? '+' : ''}${d} pz)${it.supplier_order_id ? '' : ' (riga senza ordine fornitore: non ammessa)'}`);
      }
    });
    const declared = Object.values(byDecl).reduce((a, b) => a + b, 0);
    return { byDecl, remaining, spedito, gia, declared, diffLines };
  }, [items, alloc, received]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasDifferences = totals.diffLines.length > 0;
  const nationalizedWarehouses = warehouses.filter((w) => w.is_nationalized);
  const declTitle = (d: Declaration, i: number) => `${documentTypeLabel(d.document_type)} ${d.document_number || `#${i + 1}`}`;

  // ---------------------------------------------------------------- Importa con AI
  const handleAiAnalyze = async (force = false) => {
    if (!shipment) return;
    const files = aiFiles.map((f: any) => f.originFileObj as File).filter(Boolean);
    if (!files.length) { message.warning('Selezionare la bolletta H2 (IM7) e/o H1 (IM4)'); return; }
    setAiAnalyzing(true); setAiExisting(null);
    try {
      const data = await customsAIImportAPI.analyze(shipment.id, files, force);
      setAiResult(data);
      applyAiProposal(data.proposal || {});
      setAiVisible(false);
      const undecided = (data.articles || []).filter((a: any) => a.status === 'undecided').length;
      message.success(`Bollette lette: ${data.imports.map((i: any) => documentTypeLabel(i.kind)).join(' + ')}. ${undecided ? `${undecided} articoli da decidere a mano.` : 'Tutti gli articoli ripartiti: controlla e genera.'}`, 8);
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail?.existing_import_id) setAiExisting(detail.message);
      else message.error((typeof detail === 'string' ? detail : detail?.message) || "Errore durante l'analisi delle bollette", 8);
    } finally { setAiAnalyzing(false); }
  };

  // Proposta AI → dichiarazioni e quote. Formato v3.9.0: proposal.declarations[]
  // ({key, document_type, document_number, document_date, mnr_code, warehouse_id})
  // + proposal.allocations {shipment_item_id: {key: pezzi}}; le righe "da decidere"
  // restano sulla prima H2 (o H1 se la spedizione è nazionalizzata).
  const applyAiProposal = (pr: any) => {
    // mappa chiave della proposta → dichiarazione del modal costruita PRIMA del filtro
    // (spedizione nazionalizzata: le H2 lette non diventano dichiarazioni)
    const keyMap: Record<string, string> = {};
    const decls: Declaration[] = [];
    (pr.declarations || []).forEach((d: any) => {
      const type: DeclType = d.document_type === 'IM4' ? 'IM4' : 'IM7';
      if (onlyIM4 && type === 'IM7') return;
      const decl: Declaration = {
        key: newKey(),
        document_type: type,
        document_number: d.document_number || '',
        document_date: d.document_date ? dayjs(d.document_date) : dayjs(),
        mnr_code: d.mnr_code || '',
        warehouse_id: type === 'IM4' ? (d.warehouse_id || (onlyIM4 && shipment ? shipment.warehouse_id : undefined)) : undefined,
        hint: d.source_key,
      };
      keyMap[d.key] = decl.key;
      decls.push(decl);
    });
    if (!decls.length) return;
    const fallback = (decls.find((d) => d.document_type === defaultType) || decls[0]).key;
    const a: Record<number, Record<string, number>> = {};
    items.forEach((it) => {
      const avail = availableOf(it);
      if (avail <= 0) return;
      const proposed = pr.allocations?.[it.id] ?? pr.allocations?.[String(it.id)] ?? {};
      const row: Record<string, number> = {};
      let used = 0;
      Object.entries(proposed).forEach(([k, q]) => {
        const target = keyMap[k];
        const v = Math.min(Number(q) || 0, avail - used);
        if (target && v > 0) { row[target] = (row[target] || 0) + v; used += v; }
      });
      if (avail - used > 0) row[fallback] = (row[fallback] || 0) + (avail - used);
      a[it.id] = row;
    });
    setDeclarations(decls);
    setAlloc(a);
  };

  const aiRowInfo = (itemId: number) => aiResult?.rows?.find((r: any) => r.shipment_item_id === itemId);

  // ---------------------------------------------------------------- Genera
  const doSubmit = async () => {
    if (!shipment) return;
    const payload: GenerateCustomsDeclarationsPayload = {
      declarations: declarations.map((d) => ({
        key: d.key,
        document_type: d.document_type,
        document_number: d.document_number.trim().toUpperCase(),
        document_date: (d.document_date as Dayjs).format('YYYY-MM-DD'),
        mnr_code: d.mnr_code.trim() || undefined,
        warehouse_id: d.document_type === 'IM4' ? d.warehouse_id : undefined,
      })),
      lines: items
        .map((it) => ({
          shipment_item_id: it.id,
          ...(canReceive(it) && receivedOf(it) !== it.quantity ? { received_quantity: receivedOf(it) } : {}),
          allocations: Object.entries(alloc[it.id] || {})
            .filter(([, q]) => (q || 0) > 0)
            .map(([declaration_key, quantity]) => ({ declaration_key, quantity })),
        }))
        .filter((l) => l.allocations.length > 0 || l.received_quantity !== undefined),
    };
    if (hasDifferences) {
      payload.rectification_reason_code = reasonCode || 'CUSTOMS_COUNT';
      payload.rectification_notes = reasonNotes.trim() || undefined;
    }
    setLoading(true);
    try {
      const response = await unifiedShipmentsAPI.generateCustomsDocuments(shipment.id, payload);
      if (aiResult?.imports?.length && response?.documents?.length) {
        try { await customsAIImportAPI.confirm(aiResult.imports.map((i: any) => i.import_id), response.documents.map((d: any) => d.id)); } catch { /* registro: non bloccante */ }
      }
      message.success((response?.message || 'Dichiarazioni generate')
        + ' Sono in bozza: elaborale in Documenti di Carico per far entrare la merce.', 8);
      onSuccess();
      window.location.href = '/customs-documents';
    } catch (error: any) {
      console.error('Errore generazione dichiarazioni:', error);
      message.error(error.response?.data?.detail || 'Errore durante la generazione delle dichiarazioni', 8);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (!shipment) return;
    if (!declarations.length) { message.error('Aggiungi almeno una dichiarazione'); return; }
    const errors: string[] = [];
    const numbers = new Set<string>();
    declarations.forEach((d, i) => {
      const title = `${documentTypeLabel(d.document_type)} #${i + 1}`;
      const num = d.document_number.trim().toUpperCase();
      if (!num) errors.push(`${title}: manca il numero (MRN)`);
      else if (numbers.has(num)) errors.push(`${title}: numero ${num} ripetuto`);
      numbers.add(num);
      if (!d.document_date) errors.push(`${title}: manca la data`);
      if (d.document_type === 'IM4' && !d.warehouse_id) errors.push(`${title}: manca il magazzino nazionalizzato`);
      if (!(totals.byDecl[d.key] > 0)) errors.push(`${title}: nessun pezzo assegnato (eliminala o assegnale delle righe)`);
    });
    const over = items.filter((it) => remainingOf(it) < 0);
    if (over.length) errors.push(`Righe con più pezzi assegnati di quelli da dichiarare: ${over.map((it) => it.product_code).join(', ')}`);
    if (hasDifferences && !reasonCode) errors.push('Indica il motivo della differenza tra spedito e ricevuto');
    if (errors.length) {
      Modal.error({ title: 'Controlla le dichiarazioni', content: <ul style={{ paddingLeft: 18 }}>{errors.map((e, i) => <li key={i}>{e}</li>)}</ul> });
      return;
    }
    if (totals.remaining > 0) {
      Modal.confirm({
        title: `Restano ${totals.remaining} pz da dichiarare`,
        content: 'Questi pezzi restano in transito: potrai metterli in un\'altra dichiarazione con lo stesso pulsante H2/H1. Procedere?',
        okText: 'Genera',
        cancelText: 'Annulla',
        onOk: doSubmit,
      });
      return;
    }
    doSubmit();
  };

  // ---------------------------------------------------------------- Tabelle
  const declColumns = [
    {
      title: 'Tipo', key: 'type', width: 150,
      render: (_: any, d: Declaration) => (
        <Select
          value={d.document_type}
          style={{ width: '100%' }}
          onChange={(v: DeclType) => updateDeclaration(d.key, { document_type: v, warehouse_id: v === 'IM4' && onlyIM4 && shipment ? shipment.warehouse_id : undefined })}
        >
          <Option value="IM7" disabled={onlyIM4}>{documentTypeLabel('IM7')}</Option>
          <Option value="IM4">{documentTypeLabel('IM4')}</Option>
        </Select>
      ),
    },
    {
      title: 'Numero (MRN)', key: 'number', width: 220,
      render: (_: any, d: Declaration) => (
        <div>
          <Input value={d.document_number} placeholder="Es. 26IT5CA7..." onChange={(e) => updateDeclaration(d.key, { document_number: e.target.value })} />
          {d.hint && <Text type="secondary" style={{ fontSize: 11 }}>{d.hint}</Text>}
        </div>
      ),
    },
    {
      title: 'Data', key: 'date', width: 140,
      render: (_: any, d: Declaration) => (
        <DatePicker format="DD/MM/YYYY" value={d.document_date} style={{ width: '100%' }} onChange={(v) => updateDeclaration(d.key, { document_date: v })} />
      ),
    },
    {
      title: 'MNR (opz.)', key: 'mnr', width: 130,
      render: (_: any, d: Declaration) => (
        <Input value={d.mnr_code} onChange={(e) => updateDeclaration(d.key, { mnr_code: e.target.value })} />
      ),
    },
    {
      title: 'Magazzino', key: 'wh',
      render: (_: any, d: Declaration) => d.document_type === 'IM7'
        ? <Text type="secondary">{shipment?.warehouse_name || 'magazzino doganale della spedizione'}</Text>
        : (
          <Select
            value={d.warehouse_id}
            placeholder="Magazzino nazionalizzato"
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="children"
            disabled={onlyIM4}
            onChange={(v: number) => updateDeclaration(d.key, { warehouse_id: v })}
          >
            {nationalizedWarehouses.map((w) => (
              <Option key={w.id} value={w.id}>{w.cod_mag ? `${w.cod_mag} - ${w.description}` : w.description}</Option>
            ))}
          </Select>
        ),
    },
    {
      title: 'Pz', key: 'pz', width: 80, align: 'right' as const,
      render: (_: any, d: Declaration) => <strong>{(totals.byDecl[d.key] || 0).toLocaleString('it-IT')}</strong>,
    },
    {
      title: '', key: 'del', width: 50,
      render: (_: any, d: Declaration) => (
        <Button type="text" danger icon={<DeleteOutlined />} disabled={declarations.length <= 1} onClick={() => removeDeclaration(d.key)} />
      ),
    },
  ];

  const rowColumns: any[] = [
    {
      title: 'Prodotto', key: 'product', fixed: 'left' as const, width: 260,
      render: (_: any, record: ShipmentPackingListItem) => (
        <span>
          <strong>{record.product_code || `ID ${record.product_id}`}</strong>
          {record.product_description ? ` - ${record.product_description}` : ''}
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.supplier_order_number ? `Ordine: ${record.supplier_order_number}` : ''}
            {record.invoice_number ? ` · Fattura: ${record.invoice_number}` : ''}
          </Text>
          {aiRowInfo(record.id) && (aiRowInfo(record.id).proposed
            ? <><br /><Tag color={aiRowInfo(record.id).proposed === 'IM4' ? 'blue' : 'green'}>{documentTypeLabel(aiRowInfo(record.id).proposed)} {aiRowInfo(record.id).doc_number || ''} art. {aiRowInfo(record.id).article_number}</Tag></>
            : <><br /><Tooltip title={aiRowInfo(record.id).reason}><Tag color="orange">da decidere</Tag></Tooltip></>)}
        </span>
      )
    },
    { title: 'Spedito', dataIndex: 'quantity', key: 'quantity', width: 80, align: 'right' as const },
    {
      title: 'Già dichiarato', key: 'already', width: 95, align: 'right' as const,
      render: (_: any, r: ShipmentPackingListItem) => already(r) ? <Tag>{already(r)}</Tag> : '-',
    },
    {
      title: 'Ricevuto', key: 'received', width: 105,
      render: (_: any, record: ShipmentPackingListItem) => canReceive(record) ? (
        <InputNumber
          min={1}
          value={receivedOf(record)}
          onChange={(val) => setReceivedQty(record, val)}
          status={receivedOf(record) !== record.quantity ? 'warning' : undefined}
          style={{ width: '100%' }}
        />
      ) : <Tooltip title="Riga già in parte dichiarata: il ricevuto si corregge dalla bozza o con la Rettifica Quantità"><Text type="secondary">{record.quantity}</Text></Tooltip>
    },
    ...declarations.map((d, i) => ({
      title: <Tag color={documentTypeColor(d.document_type)} style={{ margin: 0 }}>{declTitle(d, i)}</Tag>,
      key: `decl-${d.key}`,
      width: 140,
      render: (_: any, record: ShipmentPackingListItem) => (
        <InputNumber
          min={0}
          value={(alloc[record.id] || {})[d.key] || 0}
          onChange={(val) => setAllocQty(record, d.key, val)}
          disabled={availableOf(record) <= 0}
          style={{ width: '100%' }}
        />
      ),
    })),
    ...(declarations.length > 1 ? [{
      title: 'Tutto in', key: 'assign', width: 150,
      render: (_: any, record: ShipmentPackingListItem) => (
        <Select
          size="small"
          placeholder="Scegli"
          value={undefined}
          style={{ width: '100%' }}
          disabled={availableOf(record) <= 0}
          onChange={(k: string) => assignAll(record, k)}
          options={declarations.map((d, i) => ({ value: d.key, label: declTitle(d, i) }))}
        />
      ),
    }] : []),
    {
      title: 'Da dichiarare', key: 'remaining', width: 100, align: 'right' as const,
      render: (_: any, record: ShipmentPackingListItem) => {
        const r = remainingOf(record);
        if (availableOf(record) <= 0 && already(record) > 0) return <Tag color="green">dichiarata</Tag>;
        return <Text type={r < 0 ? 'danger' : r > 0 ? 'warning' : undefined} strong={r !== 0}>{r}</Text>;
      }
    },
  ];

  return (
    <Modal
      title="Genera dichiarazioni doganali H2 (IM7) / H1 (IM4)"
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      okText="Genera dichiarazioni"
      cancelText="Annulla"
      confirmLoading={loading}
      width={1200}
      style={{ top: 20 }}
      destroyOnClose
    >
      {shipment && (
        <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
          <Text type="secondary">
            Spedizione: <strong>{shipment.shipment_code}</strong>
            {shipment.description ? <> — {shipment.description}</> : null}
          </Text>
        </div>
      )}

      <Spin spinning={loadingData}>
        {onlyIM4 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Spedizione destinata a un magazzino nazionalizzato: la merce entra solo con dichiarazioni H1 (IM4)."
          />
        )}
        <Alert
          type="info"
          style={{ marginBottom: 12 }}
          message="Crea una o più dichiarazioni (H2 per il deposito doganale, H1 per la libera pratica) e ripartisci le righe. La proposta parte da una dichiarazione per fattura del fornitore: puoi unirle, dividerle o cambiarne il tipo. I pezzi non assegnati restano in transito e si dichiarano dopo. Se il ricevuto è diverso dallo spedito la partita viene rettificata."
        />

        <Space style={{ marginBottom: 12 }} wrap>
          <Tooltip title="Carica le bollette H2 / H1 (anche scansionate) tramite AI: numeri, date e ripartizione per riga vengono precompilati">
            <Button icon={<RobotOutlined />} onClick={() => setAiVisible(true)}>Importa con AI</Button>
          </Tooltip>
          {aiResult && (
            <Text type="secondary">
              Bollette lette: {aiResult.imports.map((i: any) => `${documentTypeLabel(i.kind)} ${i.mrn || ''}`).join(' · ')}
            </Text>
          )}
        </Space>
        {aiResult && (
          <div style={{ marginBottom: 12 }}>
            {(aiResult.warnings || []).map((w: string, i: number) => <Alert key={i} type="warning" showIcon style={{ marginBottom: 6 }} message={w} />)}
            <Table
              size="small"
              rowKey={(a: any) => `${a.doc}-${a.doc_number || ''}-${a.article_number}`}
              dataSource={aiResult.articles}
              pagination={false}
              style={{ marginBottom: 8 }}
              columns={[
                { title: 'Bolletta', key: 'doc', width: 190, render: (a: any) => `${documentTypeLabel(a.doc)} ${a.doc_number || ''}` },
                { title: 'Art.', dataIndex: 'article_number', width: 50 },
                { title: 'Codice merce', dataIndex: 'commodity_code', width: 110 },
                { title: 'Kg bolletta', dataIndex: 'gross_mass', width: 100, align: 'right' as const, render: (v: number) => v != null ? v.toFixed(2) : '-' },
                { title: 'Kg righe', dataIndex: 'assigned_kg', width: 90, align: 'right' as const, render: (v: number) => v ? v.toFixed(2) : '-' },
                { title: 'Colli', dataIndex: 'packages', width: 60, align: 'right' as const },
                { title: 'Valore art.', dataIndex: 'article_price', width: 100, align: 'right' as const, render: (v: number) => v != null ? v.toFixed(2) : '-' },
                { title: 'Righe', dataIndex: 'rows', width: 60, align: 'right' as const, render: (r: number[]) => r?.length || 0 },
                { title: 'Esito', render: (a: any) => (
                  <Tooltip title={a.message || ''}>
                    <Tag color={a.status === 'ok' ? 'green' : a.status === 'by_code' ? 'cyan' : a.status === 'partial' ? 'gold' : a.status === 'no_rows' ? 'default' : 'orange'}>
                      {a.status === 'ok' ? 'quadrato' : a.status === 'by_code' ? 'per codice' : a.status === 'partial' ? 'parziale' : a.status === 'no_rows' ? 'merce non in NAVI' : 'da decidere'}
                    </Tag>
                  </Tooltip>
                ) },
              ]}
            />
          </div>
        )}

        <Divider orientation="left" style={{ marginTop: 0 }}>Dichiarazioni</Divider>
        <Table
          rowKey="key"
          size="small"
          dataSource={declarations}
          columns={declColumns}
          pagination={false}
        />
        <Space style={{ marginTop: 8, marginBottom: 8 }}>
          {!onlyIM4 && <Button icon={<PlusOutlined />} onClick={() => addDeclaration('IM7')}>Aggiungi {documentTypeLabel('IM7')}</Button>}
          <Button icon={<PlusOutlined />} onClick={() => addDeclaration('IM4')}>Aggiungi {documentTypeLabel('IM4')}</Button>
        </Space>

        <Divider orientation="left">Righe della spedizione</Divider>
        <Table
          rowKey="id"
          dataSource={items}
          columns={rowColumns}
          pagination={false}
          size="small"
          scroll={{ x: 'max-content', y: 420 }}
          locale={{ emptyText: 'Nessun prodotto nella spedizione' }}
        />
        <Row gutter={16} style={{ marginTop: 8 }}>
          <Col>
            <Text>Spediti <strong>{totals.spedito.toLocaleString('it-IT')}</strong></Text>
          </Col>
          {totals.gia > 0 && (
            <Col>
              <Text>Già dichiarati <strong>{totals.gia.toLocaleString('it-IT')}</strong></Text>
            </Col>
          )}
          <Col>
            <Text>In queste dichiarazioni <strong>{totals.declared.toLocaleString('it-IT')}</strong></Text>
          </Col>
          <Col>
            <Text type={totals.remaining > 0 ? 'warning' : undefined}>Da dichiarare <strong>{totals.remaining.toLocaleString('it-IT')}</strong></Text>
          </Col>
        </Row>

        {hasDifferences && (
          <>
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
              message="Quantità ricevute diverse dallo spedito: la partita verrà rettificata prima di generare le dichiarazioni"
              description={<ul style={{ margin: 0, paddingLeft: 18 }}>{totals.diffLines.map((l, i) => <li key={i}>{l}</li>)}</ul>}
            />
            <Row gutter={16} style={{ marginTop: 12 }}>
              <Col span={10}>
                <Text>Motivo della differenza</Text>
                <Select value={reasonCode} onChange={setReasonCode} style={{ width: '100%' }}>
                  {reasons.map((r) => <Option key={r.code} value={r.code}>{r.label}</Option>)}
                </Select>
              </Col>
              <Col span={14}>
                <Text>Note rettifica (opzionali)</Text>
                <Input className="no-uppercase-modal" value={reasonNotes} onChange={(e) => setReasonNotes(e.target.value)} placeholder="Es. conteggio in dogana" />
              </Col>
            </Row>
          </>
        )}
      </Spin>
      <Modal
        open={aiVisible}
        title={<span><RobotOutlined /> Importa con AI - dichiarazioni H2 (IM7) / H1 (IM4)</span>}
        onCancel={() => setAiVisible(false)}
        footer={[
          <Button key="c" onClick={() => setAiVisible(false)}>Annulla</Button>,
          <Button key="a" type="primary" icon={<RobotOutlined />} loading={aiAnalyzing} disabled={!aiFiles.length} onClick={() => handleAiAnalyze(false)}>Analizza con AI</Button>,
        ]}
        className="no-uppercase-modal"
      >
        <Upload.Dragger accept=".pdf,.png,.jpg,.jpeg" multiple fileList={aiFiles} beforeUpload={() => false}
          onChange={({ fileList: fl }) => { setAiFiles(fl.slice(0, 10)); setAiExisting(null); }} onRemove={(f) => { setAiFiles(aiFiles.filter((x: any) => x.uid !== f.uid)); return true; }}>
          <p className="ant-upload-drag-icon"><InboxOutlined /></p>
          <p className="ant-upload-text">Trascina qui le dichiarazioni H2 e/o H1 (PDF anche scansionati, fino a 10 file, anche più dello stesso tipo)</p>
          <p className="ant-upload-hint">NAVI legge MRN, date, fattura citata e articoli, e propone per ogni riga della spedizione la dichiarazione in cui è entrata: prima per fattura del fornitore, poi quando codice merce, kg e valore tornano; gli articoli dubbi restano da decidere a mano. Massimo 30 pagine per file.</p>
        </Upload.Dragger>
        {aiExisting && <Alert type="warning" showIcon style={{ marginTop: 12 }} message={aiExisting} action={<Button size="small" type="primary" loading={aiAnalyzing} onClick={() => handleAiAnalyze(true)}>Rianalizza comunque</Button>} />}
      </Modal>
    </Modal>
  );
};

export default CustomsSplitModal;
